import { useMemo } from "react";
import clsx from "clsx";

import CustomizableChart from "#/components/chart";
import { STATUS_CONFIG, DEFAULT_STATUS } from "./status-config";
import { formatKPIValue } from "./helper";

import type { FC } from "react";
import type { SummaryInfoProps } from "./types";
import type { ChartConfig } from "#/components/chart/types";

const PRINT_COLORS = {
  correct: "#000000",
  incorrect: "#EAECF0",
  noAnswer: "#98A2B3"
} as const;

const SCREEN_COLORS = {
  correct: "#10B981",
  incorrect: "#EF4444",
  noAnswer: "#9CA3AF"
} as const;

const getStatus = (label: string) => {
  return STATUS_CONFIG[label] || DEFAULT_STATUS;
};

const SummaryInfo: FC<SummaryInfoProps> = ({
  mark,
  markWN,
  corrects = 0,
  inCorrects = 0,
  noAnswers = 0,
  performanceLabel,
  answerRate,
  accuracyRate,
  className = "",
  subTitle = "",
  isPrint = false,
  onChartSegmentClick
}) => {
  const total = (corrects ?? 0) + (inCorrects ?? 0) + (noAnswers ?? 0);

  const pieChartData = [
    {
      name: "درست",
      value: corrects,
      color: isPrint ? PRINT_COLORS.correct : SCREEN_COLORS.correct,
      percentage: (corrects / total) * 100
    },
    {
      name: "غلط",
      value: inCorrects,
      color: isPrint ? PRINT_COLORS.incorrect : SCREEN_COLORS.incorrect,
      percentage: (inCorrects / total) * 100
    },
    {
      name: "نزده",
      value: noAnswers,
      color: isPrint ? PRINT_COLORS.noAnswer : SCREEN_COLORS.noAnswer,
      percentage: (noAnswers / total) * 100
    }
  ] as const;

  const kpis = [
    {
      label: "نمره (بدون نمره منفی)",
      value: markWN,
      suffix: "%",
      decimalPlaces: 2,
      color: "#374151"
    },
    {
      label: "نرخ پاسخ‌دهی",
      value: answerRate,
      suffix: "%",
      decimalPlaces: 0,
      color: "#10B981"
    },
    {
      label: "دقت پاسخ‌دهی",
      value: accuracyRate,
      suffix: "%",
      decimalPlaces: 2,
      color: "#3B82F6"
    }
  ] as const;

  const finalScore = {
    value: mark,
    label: "",
    status: getStatus(performanceLabel ?? "نا مشخص")
  } as const;

  const data = {
    title: "نمره نهایی",
    kpis,
    pieChart: {
      title: "نمره نهایی",
      subtitle: subTitle || undefined,
      data: pieChartData,
      finalScore
    }
  } as const;

  // Transform pie chart data for the chart component
  const chartData = useMemo(() => {
    return data.pieChart.data.map((item) => ({
      name: item.name,
      value: item.value,
      color: item.color,
      visible: true
    }));
  }, [data.pieChart.data]);

  const chartConfig: ChartConfig = {
    showPercentages: false,
    showLabels: true,
    responsive: false,
    rtl: true, // Enable RTL for Persian text
    animation: {
      duration: 1000,
      easing: "ease-out"
    },
    size: {
      width: 180,
      height: 180
    },
    tooltip: {
      show: true,
      formatter: (value: number, name: string): [string, string] => {
        const item = data.pieChart.data.find((d) => d.name === name);
        return [`${value} (${item?.percentage.toFixed(2)}%)`, name];
      }
    },
    legend: {
      show: false // ! We'll use our custom legend
    },
    accessibility: {
      ariaLabel: "نمودار نتایج آزمون",
      keyboardNavigation: true
    }
  } as const;

  return (
    <div className={clsx("w-full", className)}>
      {/* Main */}
      <div className="flex justify-between gap-6 print:gap-2">
        {/* Pie Chart Section */}
        <div className="flex flex-col items-center w-full h-[232px] print:!h-[200px]">
          <div className="flex items-center justify-between bg-[#fff9f5] border border-[#ff6200] h-full rounded-xl py-6 px-11 w-full max-w-full print:!px-1">
            {/* Final Score */}
            <div className="text-center flex flex-col align-center">
              <div className="flex items-center gap-1">
                <h3 className="text-[20px] font-semibold text-neutral-700 whitespace-nowrap">
                  {data.pieChart.title}
                </h3>

                {data.pieChart.subtitle && (
                  <p className="font-medium text-neutral-700 whitespace-nowrap">
                    {data.pieChart.subtitle}
                  </p>
                )}
              </div>
              <p
                dir="ltr"
                className="text-[42px] font-semibold mb-3 mt-4 whitespace-nowrap print:text-2xl"
                style={{ color: data.pieChart.finalScore.status.color }}
              >
                {`${(data.pieChart.finalScore.value ?? 0).toFixed(2).replace(".", "/")} %`}
              </p>

              <p
                className={clsx(
                  "w-[190px] px-2 print:w-fit border h-8 flex items-center justify-center rounded-2 text-xs font-medium",
                  "relative overflow-hidden",
                  "before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full",
                  "before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent",
                  "before:animate-shimmer"
                )}
                style={{
                  color: data.pieChart.finalScore.status.color,
                  backgroundColor:
                    data.pieChart.finalScore.status.backgroundColor,
                  borderColor: data.pieChart.finalScore.status.color
                }}
              >
                {data.pieChart.finalScore.status.text}
              </p>
            </div>

            {/* Chart */}
            <CustomizableChart
              data={chartData}
              config={chartConfig}
              className=""
              onSegmentClick={(segmentData) => {
                const pieChartItem = data.pieChart.data.find(
                  (item) => item.name === segmentData.name
                );
                if (pieChartItem && onChartSegmentClick) {
                  onChartSegmentClick(pieChartItem);
                }
              }}
            />

            {/* Legend */}
            <div className="flex flex-col gap-4">
              {data.pieChart.data.map((item) => (
                <div
                  key={`legend-${item.name}`}
                  className="flex items-center w-fit h-6 gap-1"
                >
                  <div
                    className="w-[10px] h-[10px] rounded-1"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="w-[110px] flex items-center justify-between gap-4 text-sm font-medium whitespace-nowrap text-[#202020]">
                    <span className="w-10">{item.name}:</span>

                    <span className="inline-block">
                      {item.percentage.toFixed(2)}%
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KPIs Section */}
        <div className="flex flex-col justify-between gap-3 w-full">
          {data.kpis.map((kpi, index) => (
            <div
              key={`kpi-${kpi.label}-${index}`}
              className="w-full border rounded-3 border-neutral-300 bg-neutral-25 flex justify-between gap-2 items-center px-6 h-16 print:!px-1 print:gap-1"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              <span className="text-sm font-medium text-neutral-700">
                {kpi.label}
              </span>

              <p
                dir="ltr"
                className="text-[20px] print:text-base font-medium text-neutral-700"
              >
                {formatKPIValue(kpi)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SummaryInfo;
